import {
  StateDefinition,
  StateIndex,
} from '@iwentsourcing/core/storage/state-declaration.js';
import { BrowseResult } from '@iwentsourcing/core/storage/browse-result.js';
import { ObserveRangeOptions } from '@iwentsourcing/core/storage/observer/observe-range-options.js';
import { IterateOptions } from '@iwentsourcing/core/storage/observer/iterate-options.js';
import { RemoteDescription } from './remote-description.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { SessionManager } from '../auth/jwt-session.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';
import { StateValue } from '@iwentsourcing/core/storage/state-value.js';
import { mapToState } from '@iwentsourcing/core/runtime/get-state-data-keys.js';
import { ObservedIndexClient } from '@iwentsourcing/core/storage/observer/observed-index-client.js';
import { DataSet } from '@iwentsourcing/core/storage/data-set.js';
import { FilterStateIndex } from '@iwentsourcing/core/storage/filter-state-index.js';
import { FilterValue } from '@iwentsourcing/core/storage/filter-value.js';
import { FilterKey } from '@iwentsourcing/core/storage/filter-key.js';
import { Telemetry } from '@iwentsourcing/core/tracer/telemetry.js';

export function createRemoteObservedIndexClient<
  TState extends StateDefinition<any, any, any>,
  TIndex extends StateIndex<any, any>,
>(
  state: TState,
  index: TIndex,
  keyIndex: number,
  rpcClient: RpcClient,
  session: SessionManager,
  lazyVersion: Lazy<string>,
  type: 'stream' | 'aggregation',
  streamIdentifier: RemoteDescription,
  stateName: string,
  indexName: string,
  filter: Record<string, StateValue>,
  telemetry: Telemetry,
): ObservedIndexClient<TState, TIndex> {
  return {
    filter(
      key: FilterKey<TIndex> & string,
      value: FilterValue<TState, TIndex>,
    ): ObservedIndexClient<TState, FilterStateIndex<TIndex>> {
      return createRemoteObservedIndexClient(
        state,
        index,
        keyIndex + 1,
        rpcClient,
        session,
        lazyVersion,
        type,
        streamIdentifier,
        stateName,
        indexName,
        { ...filter, [key]: value },
        telemetry,
      );
    },
    async *iterate(
      bookmark: FilterValue<TState, TIndex> | null,
      iterateOptions: IterateOptions,
    ): AsyncGenerator<DataSet<any>> {
      const iterator = rpcClient['state.index.iterate'].call(
        {
          tenant: streamIdentifier.tenant,
          realm: streamIdentifier.realm,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          index: indexName,
          state: stateName,
          args: streamIdentifier.args,
          bookmark: bookmark ?? undefined,
          direction: iterateOptions.dir,
          open: iterateOptions.open,
          filter,
          token: await session.resolveToken(),
          type,
          take: undefined,
        },
        iterateOptions.signal,
      );

      for await (const item of iterator) {
        yield {
          data: mapToState(state, item.data, telemetry),
          keys: item.keys,
        };
      }
    },
    async *observeRange(
      bookmark: FilterValue<TState, TIndex> | null,
      options: ObserveRangeOptions,
    ): AsyncGenerator<BrowseResult<any>> {
      const iterator = rpcClient['state.index.browse.observe'].call(
        {
          tenant: streamIdentifier.tenant,
          realm: streamIdentifier.realm,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          state: stateName,
          index: indexName,
          bookmark: bookmark ?? undefined,
          direction: options?.dir,
          take: options?.take,
          filter,
          args: streamIdentifier.args,
          token: await session.resolveToken(),
          type,
          open: options.open,
        },
        options.signal,
      );
      for await (const item of iterator) {
        yield {
          items: item.items.map((i) => ({
            data: mapToState(state, i.data, telemetry),
            keys: i.keys,
          })),
          bookmark: item.bookmark,
        };
      }
    },
  };
}
